import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney, fmtQty } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
];

const StockValuationReport = () => {
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/inventory/category/list.php?page=1&limit=100&active_only=1')
            .then((res) => { if (mounted) setCategories(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Stock Valuation Report"
            subtitle="Current stock quantities and values (qty x cost price)"
            breadcrumb={["Reports", "Inventory"]}
            fetchUrl="/reports/inventory/stock_valuation.php"
            rowKey="invi_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 6, placeholder: "Code, item name" },
                {
                    type: "select", key: "ic_id", label: "Category", col: 5,
                    options: categories.map((c) => ({ value: c.ic_id, label: c.ic_name })),
                    showSearch: true,
                },
                { type: "select", key: "status", label: "Status", col: 4, options: STATUS_OPTIONS },
            ]}
            columns={[
                { title: 'Item Code', dataIndex: 'inv_code', key: 'inv_code', width: 110, sorter: (a, b) => (a.inv_code || '').localeCompare(b.inv_code || '') },
                { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', sorter: (a, b) => (a.item_name || '').localeCompare(b.item_name || '') },
                { title: 'Category', dataIndex: 'category_name', key: 'category_name', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Available Qty', dataIndex: 'available_qty', key: 'available_qty', align: 'right', width: 110, sorter: (a, b) => (a.available_qty || 0) - (b.available_qty || 0), render: (v) => fmtQty(v) },
                { title: 'UOM', dataIndex: 'uom_name', key: 'uom_name', width: 80, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Cost Price', dataIndex: 'cost_price', key: 'cost_price', align: 'right', width: 120, sorter: (a, b) => (a.cost_price || 0) - (b.cost_price || 0), render: (v) => fmtMoney(v) },
                { title: 'Stock Value', dataIndex: 'stock_value', key: 'stock_value', align: 'right', width: 130, sorter: (a, b) => (a.stock_value || 0) - (b.stock_value || 0), render: (v) => fmtMoney(v) },
            ]}
            tableSummary={(pageData) => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5}><b>Total ({pageData.length} items)</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.cost_price || 0), 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.stock_value || 0), 0))}</b></Table.Summary.Cell>
                </Table.Summary.Row>
            )}
            pdf={{
                title: "STOCK VALUATION REPORT",
                subtitle: "Current stock quantities and values (qty x cost price)",
                columns: [
                    { header: "Item Code" }, { header: "Item Name" }, { header: "Category" },
                    { header: "Available Qty", align: "right" }, { header: "UOM" },
                    { header: "Cost Price", align: "right" }, { header: "Stock Value", align: "right" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.inv_code || "-",
                        r.item_name || "-",
                        r.category_name || "—",
                        fmtQty(r.available_qty),
                        r.uom_name || "-",
                        fmtMoney(r.cost_price),
                        fmtMoney(r.stock_value),
                    ]),
                summary: (rows) => [
                    { label: "Total Stock Value:", value: fmtMoney(rows.reduce((s, r) => s + Number(r.stock_value || 0), 0)) },
                    { label: "Total Items:", value: String(rows.length) },
                ],
                totalRow: (rows) => [
                    "TOTAL", "", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.stock_value || 0), 0)),
                ],
                columnStyles: { 3: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
            }}
            excel={{
                filename: "stock-valuation-report",
                sheetName: "Stock Valuation",
                columns: [
                    { title: "Item Code", dataIndex: "inv_code", width: 14 },
                    { title: "Item Name", dataIndex: "item_name", width: 30 },
                    { title: "Category", dataIndex: "category_name", width: 20 },
                    { title: "Available Qty", dataIndex: "available_qty", width: 12 },
                    { title: "UOM", dataIndex: "uom_name", width: 8 },
                    { title: "Cost Price", dataIndex: "cost_price", width: 14 },
                    { title: "Stock Value", dataIndex: "stock_value", width: 14 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        available_qty: fmtQty(r.available_qty),
                        cost_price: fmtMoney(r.cost_price),
                        stock_value: fmtMoney(r.stock_value),
                    })),
                extraRows: (rows) => [["TOTAL", "", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.stock_value || 0), 0))]],
            }}
        />
    );
};

export default StockValuationReport;
